import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MobileAppTestingApi } from './rest/mobile-app-testing.api';
import {
  IMobileApp,
  IMobileAppCommandRequest,
  IMobileAppCommandResponse,
  IMobileAppScanResponse,
  IMobileAppSession,
  IMobileCreateSessionRequest,
  IMobileScanSaveRequest,
  IMobileGetDevicesQuery,
  IMobileScanRequest,
  IMobileDeviceResponse,
  IMobileGenerateSessionTokenResponse,
  MobileAppSessionCloseReason,
} from '../../../shared/interfaces/mobile-app.interface';

@Injectable({
  providedIn: 'root',
})
export class MobileAppTestingService {
  public constructor(private mobileAppTestingApi: MobileAppTestingApi) {}

  public getMobileApps(workspaceId: string, digitalPropertyId: string): Observable<IMobileApp[]> {
    return this.mobileAppTestingApi.getMobileApps(workspaceId, digitalPropertyId);
  }

  public getMobileDevices(
    workspaceId: string,
    digitalPropertyId: string,
    query: IMobileGetDevicesQuery,
  ): Observable<IMobileDeviceResponse> {
    return this.mobileAppTestingApi.getMobileDevices(workspaceId, digitalPropertyId, query);
  }

  public uploadMobileApp(workspaceId: string, digitalPropertyId: string, formData: FormData): Observable<IMobileApp> {
    return this.mobileAppTestingApi.uploadMobileApp(workspaceId, digitalPropertyId, formData);
  }

  public removeMobileApp(workspaceId: string, digitalPropertyId: string, mobileAppId: string): Observable<void> {
    return this.mobileAppTestingApi.deleteMobileApp(workspaceId, digitalPropertyId, mobileAppId);
  }

  public getSession(
    workspaceId: string,
    digitalPropertyId: string,
    mobileAppId: string,
    sessionId: string,
  ): Observable<IMobileAppSession> {
    return this.mobileAppTestingApi.getSession(workspaceId, digitalPropertyId, mobileAppId, sessionId);
  }

  public getSessionInProgress(
    workspaceId: string,
    digitalPropertyId: string,
    mobileAppIds: string[],
    useLoadingIndicator: boolean = true,
  ): Observable<IMobileAppSession> {
    return this.mobileAppTestingApi.getSessionInProgress(workspaceId, digitalPropertyId, mobileAppIds, useLoadingIndicator);
  }

  public startTestingMobileApp(
    workspaceId: string,
    digitalPropertyId: string,
    mobileAppId: string,
    mobileAppCreateSessionRequest: IMobileCreateSessionRequest,
  ): Observable<IMobileAppSession> {
    return this.mobileAppTestingApi.startTestingMobileApp(
      workspaceId,
      digitalPropertyId,
      mobileAppId,
      mobileAppCreateSessionRequest,
    );
  }

  public endTestingMobileApp(
    workspaceId: string,
    digitalPropertyId: string,
    mobileAppId: string,
    mobileAppSessionId: string,
    closeReason?: MobileAppSessionCloseReason,
  ): Observable<void> {
    return this.mobileAppTestingApi.endTestingMobileApp(
      workspaceId,
      digitalPropertyId,
      mobileAppId,
      mobileAppSessionId,
      closeReason,
    );
  }

  public saveTestingMobileApp(
    workspaceId: string,
    digitalPropertyId: string,
    mobileAppId: string,
    mobileAppSessionId: string,
    scanResults: IMobileScanSaveRequest,
  ): Observable<void> {
    return this.mobileAppTestingApi.saveTestingMobileApp(
      workspaceId,
      digitalPropertyId,
      mobileAppId,
      mobileAppSessionId,
      scanResults,
    );
  }

  public sendCommand(
    workspaceId: string,
    digitalPropertyId: string,
    sessionId: string,
    request: IMobileAppCommandRequest,
  ): Observable<IMobileAppCommandResponse> {
    return this.mobileAppTestingApi.sendCommand(workspaceId, digitalPropertyId, sessionId, request);
  }

  public scan(
    workspaceId: string,
    digitalPropertyId: string,
    sessionId: string,
    request: IMobileScanRequest,
  ): Observable<IMobileAppScanResponse> {
    return this.mobileAppTestingApi.scan(workspaceId, digitalPropertyId, sessionId, request);
  }

  public generateSessionToken(
    workspaceId: string,
    digitalPropertyId: string,
    mobileAppSessionId: string,
  ): Observable<IMobileGenerateSessionTokenResponse> {
    return this.mobileAppTestingApi.generateSessionToken(workspaceId, digitalPropertyId, mobileAppSessionId);
  }
}
